import { Pic } from "@/components/site/Pic";

import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileCheck2, Factory, HardHat, Ruler } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { TrustBand } from "@/components/site/TrustBand";
import { RelatedProducts } from "@/components/site/RelatedProducts";
import { MaskLines, Reveal, SectionLabel } from "@/components/site/Motion";
import { PROJECTS } from "@/components/site/Projects";
import { PublicBodyForm } from "@/components/site/PublicBodyForm";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/pageMeta";

const WORK_IDS = ["p29", "p10", "p12", "p19", "p13", "p27", "p11", "p18", "p21", "p17", "p24"];
const WORKS = WORK_IDS.map((id) => PROJECTS.find((p) => p.id === id)).filter(Boolean);
const INTERIORS = ["cant-palazzetto-reticolare", "cant-teatro", "cant-palestra-archi"];

const POINTS = [
  ["k0", Ruler, "Supporto a progetto e gara", "Affianchiamo uffici tecnici e progettisti con calcoli, dettagli costruttivi e voci di capitolato per la parte in legno."],
  ["k1", Factory, "Produzione certificata", "Legno lamellare prodotto nei nostri stabilimenti, con qualità CE certificata e legno da filiera PEFC."],
  ["k2", HardHat, "Montaggio con squadre nostre", "Pianifichiamo il cantiere e montiamo con le nostre squadre: ponti, coperture e grandi luci posati con precisione."],
  ["k3", FileCheck2, "Un unico interlocutore", "Dalla progettazione alla posa in opera, un solo referente per l'ente: meno passaggi, tempi certi."],
];

const WorkCard = ({ p, t }) => (
  <Link to={`/?progetto=${p.id}`} className="group block" data-testid={`public-work-${p.id}`}>
    <div className="aspect-[4/3] overflow-hidden">
      <Pic src={p.img} alt={t(`proj.${p.id}`, p.title)} loading="lazy" className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
    </div>
    <h3 className="mt-4 font-display text-lg font-semibold tracking-tight sm:text-xl">{t(`proj.${p.id}`, p.title)}</h3>
    <span className="mt-1 inline-flex items-center gap-1 font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]">{t("pa.see", "Vedi il progetto")} <ArrowUpRight className="h-3.5 w-3.5" /></span>
  </Link>
);

const Hero = ({ t }) => (
  <section className="px-5 pb-20 pt-10 sm:px-8" data-testid="public-hero">
    <div className="mx-auto max-w-[1500px]">
      <Link to="/" className="tap inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#D6D0C4] transition-colors duration-300 hover:text-[#D98A52]" data-testid="public-page-back">
        <ArrowLeft className="h-4 w-4" /> {t("beachPage.back", "Torna alla home")}
      </Link>
      <div className="mt-14"><SectionLabel index="PA" dark>{t("pa.label", "Comuni ed enti pubblici")}</SectionLabel></div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
        <MaskLines
          as="h1"
          className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-7 lg:text-6xl"
          lines={[t("pa.h1", "Opere pubbliche"), <span key="h">{t("pa.h2a", "in legno")} <em className="font-serif font-normal text-[#D98A52]">{t("pa.h2em", "lamellare.")}</em></span>]}
        />
        <Reveal delay={0.2} className="max-w-md lg:col-span-5">
          <p className="text-base leading-relaxed text-[#D6D0C4] sm:text-lg">{t("pa.intro", "Ponti e passerelle, scuole, palestre, palazzetti, piscine e auditorium: progettiamo, produciamo e montiamo strutture in legno lamellare per comuni ed enti pubblici, con supporto tecnico per progetti, capitolati e gare.")}</p>
          <button onClick={() => scrollToId("#richiesta-ente")} className="btn-amber mt-8" data-testid="public-hero-cta">
            {t("pa.cta", "Richiedi una valutazione tecnica")} <ArrowUpRight className="h-4 w-4" />
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function EntiPubblici() {
  const t = useT();
  usePageMeta({
    title: t("pa.metaTitle", "Opere pubbliche in legno lamellare per comuni ed enti"),
    desc: t("pa.metaDesc", "Emma Industrie realizza per comuni ed enti pubblici ponti e passerelle, scuole, palestre, palazzetti e piscine in legno lamellare: progettazione, produzione certificata e montaggio."),
    path: "/enti-pubblici",
    image: "/og-enti.jpg",
  });
  return (
    <div className="bg-[#1A1714] text-[#F3EFE6]" data-testid="public-page">
      <Nav />
      <main className="pt-36">
        <Hero t={t} />
        <section className="px-5 pb-24 sm:px-8" data-testid="public-points">
          <div className="mx-auto grid max-w-[1500px] gap-10 md:grid-cols-2 lg:grid-cols-4">
            {POINTS.map(([id, Icon, title, text], i) => (
              <Reveal key={id} delay={i * 0.08} className="border-t border-white/15 pt-6" data-testid={`public-point-${id}`}>
                <Icon className="h-6 w-6 text-[#D98A52]" />
                <h3 className="mt-4 font-display text-xl font-bold">{t(`pa.${id}.t`, title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#D6D0C4] sm:text-base">{t(`pa.${id}.d`, text)}</p>
              </Reveal>
            ))}
          </div>
        </section>
        <section className="px-5 pb-24 sm:px-8" data-testid="public-works">
          <div className="mx-auto max-w-[1500px]">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("pa.works", "Opere realizzate")}</h2>
            <div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {WORKS.map((p) => <WorkCard key={p.id} p={p} t={t} />)}
            </div>
            <div className="mt-16 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="public-interiors">
              {INTERIORS.map((n) => (
                <div key={n} className="aspect-[3/2] overflow-hidden">
                  <Pic src={`/img/emma/${n}.jpg`} alt={t("pa.intAlt", "Interno di un'opera pubblica con struttura in legno lamellare Emma Industrie")} loading="lazy" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
        <TrustBand />
        <section id="richiesta-ente" className="grain bg-[#121110] px-5 py-28 sm:px-8" data-testid="public-form-section">
          <div className="mx-auto grid max-w-[1500px] gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionLabel index="→" dark>{t("pa.formLabel", "Richiesta per enti")}</SectionLabel>
              <h2 className="mt-8 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("pa.formH", "Raccontateci l'opera.")}</h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#D6D0C4]">{t("pa.formP", "Indicate il tipo di opera e la fase in cui vi trovate: il nostro ufficio tecnico vi ricontatta entro 48 ore lavorative. Potete allegare elaborati, planimetrie o il capitolato.")}</p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7"><PublicBodyForm /></div>
          </div>
        </section>
        <RelatedProducts />
      </main>
      <Footer />
    </div>
  );
}
